import i18n from '../i18n';

export type CurrencyCode = 'KRW' | 'USD';

type FormatDisplayCurrencyOptions = {
  useSymbol?: boolean;
};

export const extractDigits = (raw: string) => raw.replaceAll(/[^0-9]/g, '');

export const parseCurrencyNumber = (raw: string) => {
  const digits = extractDigits(raw);
  if (!digits) return 0;
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
};

export const formatCurrencyInput = (raw: string, currency: CurrencyCode) => {
  const digits = extractDigits(raw);
  if (!digits) return '';
  const n = Number(digits);
  if (!Number.isFinite(n)) return '';
  const formatted = n.toLocaleString('en-US');
  return currency === 'KRW' ? `${formatted}${i18n.t('common.units.won')}` : `$${formatted}`;
};

export const normalizeCurrencyCode = (currency?: string): CurrencyCode => (
  String(currency ?? '').toUpperCase() === 'USD' ? 'USD' : 'KRW'
);

export const formatDisplayCurrency = (
  value: number,
  currency?: string,
  options?: FormatDisplayCurrencyOptions,
) => {
  const amount = Number.isFinite(value) ? value : 0;
  const currencyCode = normalizeCurrencyCode(currency);
  const locale = currencyCode === 'USD' ? 'en-US' : 'ko-KR';
  const formatted = new Intl.NumberFormat(locale).format(amount);

  if (currencyCode === 'USD') {
    return `$${formatted}`;
  }

  return options?.useSymbol ? `₩${formatted}` : `${formatted}${i18n.t('common.units.won')}`;
};

export const formatKoreanCurrency = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '';
  const units = [
    { value: 1e12, label: i18n.t('common.currency.compact.trillion') },
    { value: 1e8, label: i18n.t('common.currency.compact.hundredMillion') },
    { value: 1e4, label: i18n.t('common.currency.compact.tenThousand') },
  ];
  let remaining = Math.floor(value);
  const parts: string[] = [];

  for (const unit of units) {
    const part = Math.floor(remaining / unit.value);
    if (part > 0) {
      parts.push(`${part.toLocaleString('ko-KR')}${unit.label}`);
      remaining %= unit.value;
    }
  }

  if (parts.length === 0) {
    return `${remaining.toLocaleString('ko-KR')}${i18n.t('common.units.won')}`;
  }

  if (remaining > 0) {
    parts.push(`${remaining.toLocaleString('ko-KR')}`);
  }

  return `${parts.join(' ')}${i18n.t('common.units.won')}`;
};
